<template>
  <div class="scanner-container">
    <!-- Quick Register Button -->
    <div class="content-box">
      <button @click="openQuickScanner">
        Scan & Register (Next User)
      </button>
    </div>

    <!-- Success Banner -->
    <div v-if="showSuccess" class="success-banner">
      {{ successMessage }}
    </div>

    <!-- Search Bar + Count -->
    <div class="search-bar-wrapper">
      <input
          v-model="searchQuery"
          placeholder="Search by name or email"
          class="search-input"
      />
      <AssignableDeviceList/>
      <span class="user-count">
    {{ filteredUsers.length }} unregistered {{ filteredUsers.length === 1 ? 'user' : 'users' }}
  </span>
    </div>

    <!-- Users Table -->
    <table class="users-table">
      <thead>
      <tr>
        <th>Name</th>
        <th>Email</th>
        <th>Action</th>
      </tr>
      </thead>
      <tbody>
      <tr v-for="user in filteredUsers" :key="user.id">
        <td>{{ user.firstName }} {{ user.lastName }}</td>
        <td>{{ user.email }}</td>
        <td>
          <button @click="openScannerForUser(user)">Scan QR Code</button>
        </td>
      </tr>
      </tbody>
    </table>

    <!-- Users with Shipping Errors -->
    <div v-if="Object.keys(userErrors).length > 0" class="error-section">
      <h3>Users with Shipping Errors</h3>
      <table class="error-table">
        <thead>
        <tr>
          <th>Name</th>
          <th>Email</th>
          <th>Error</th>
          <th>Action</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="(errorMessage, userId) in userErrors" :key="userId">
          <td>{{ getUserById(userId)?.firstName }} {{ getUserById(userId)?.lastName }}</td>
          <td>{{ getUserById(userId)?.email }}</td>
          <td class="error-message">{{ errorMessage }}</td>
          <td>
            <button @click="retryShipping(userId)" class="retry-btn">Retry Shipping</button>
            <button @click="clearUserError(userId)" class="clear-btn">Clear Error</button>
          </td>
        </tr>
        </tbody>
      </table>
    </div>

    <!-- QR Scanner - Always rendered, CSS hidden/shown -->
    <div class="scanner-overlay" :class="{ 'scanner-visible': showScanner }">
      <div class="scanner-content">
        <div class="scanner-user-info" v-if="scanningUser">
          <p><strong>Registering:</strong> {{ scanningUser.firstName }} {{ scanningUser.lastName }} ({{ scanningUser.email }})</p>
        </div>

        <!-- QR Scanner - never destroyed, just CSS hidden/shown -->
        <qrcode-stream
            @decode="onDecode"
            @init="onInit"
            ref="qrScanner"
        />

        <div v-if="cameraError" class="error-text">
          Error accessing camera: {{ cameraError.message }}
        </div>

        <div v-if="result" class="result-text">
          Scanned: {{ result }}
        </div>
        <div v-if="errorMessage" class="error-text">
          {{ errorMessage }}
        </div>

        <div class="scanner-buttons">
          <!-- Confirmation Step -->
          <button
              v-if="confirmingOverwrite"
              :disabled="confirmationTimer > 0 || isSubmitting"
              @click="submitRegistration"
              class="submit-btn"
          >
            <span v-if="isSubmitting" class="spinner"></span>
            {{ isSubmitting ? 'Processing...' : (confirmationTimer > 0 ? `Confirm in ${confirmationTimer}s...` : 'Assign this device to another user?') }}
          </button>

          <!-- Default Action Button -->
          <button
              v-else
              @click="submitRegistration"
              :disabled="!result || isSubmitting"
              class="submit-btn"
          >
            <span v-if="isSubmitting" class="spinner"></span>
            {{ isSubmitting ? 'Processing...' : 'Use This Code' }}
          </button>

          <button @click="closeScanner" :disabled="isSubmitting">Cancel</button>
          
          <!-- Clear Error Button -->
          <button 
              v-if="errorMessage && !isSubmitting" 
              @click="clearErrorAndRescan" 
              class="clear-error-btn"
          >
            Clear Error & Scan Again
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
.error-text {
  color: #ff6b6b;
  margin-top: 10px;
  font-weight: bold;
}

.success-banner {
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  background-color: #4caf50;
  color: white;
  padding: 12px 24px;
  border-radius: 6px;
  font-size: 1em;
  z-index: 1000;
  box-shadow: 0 2px 6px rgba(0,0,0,0.3);
  transition: opacity 0.3s ease-in-out;
}

.scanner-container {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  min-height: 80vh;
  text-align: center;
}

.content-box {
  max-width: 600px;
  padding: 20px;
}

button {
  margin: 10px;
  padding: 10px 20px;
  font-size: 1em;
}

/* Scanner styling - CSS show/hide instead of modal */
.scanner-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0,0,0,0.9);
  z-index: 999;
  
  /* Hidden by default */
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.3s ease, visibility 0.3s ease;
}

.scanner-overlay.scanner-visible {
  /* Shown when scanner is active */
  opacity: 1;
  visibility: visible;
}

.scanner-content {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: #222;
  color: #f0f0f0;
  padding: 30px;
  border-radius: 10px;
  text-align: center;
  width: 90%;
  max-width: 500px;
  box-shadow: 0 0 20px rgba(0,0,0,0.5);
}

.scanner-buttons {
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.scanner-buttons button {
  background-color: #444;
  color: white;
  border: none;
  padding: 10px 20px;
  margin: 10px 5px;
  font-size: 1em;
  width: 100%;
  max-width: 300px;
  border-radius: 5px;
  cursor: pointer;
}

.scanner-buttons button:hover {
  background-color: #666;
}

.scanner-buttons button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.scanner-user-info {
  margin-bottom: 15px;
  font-size: 1rem;
}

/* Loading spinner */
.spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #333;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-right: 8px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.submit-btn {
  display: flex;
  align-items: center;
  justify-content: center;
}

.clear-error-btn {
  background-color: #ff6b6b !important;
  color: white;
}

.clear-error-btn:hover {
  background-color: #ff5252 !important;
}

.scanner-buttons button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.search-box {
  margin: 20px 0;
  text-align: center;
}

.search-input {
  padding: 8px 12px;
  font-size: 1em;
  width: 300px;
  max-width: 90%;
  border-radius: 5px;
  border: 1px solid #ccc;
  box-sizing: border-box;
}

.search-bar-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  margin: 20px 0;
}

.user-count {
  font-size: 1em;
  color: #ccc;
}

.users-table {
  width: 90%;
  background-color: #2c2c2c; /* dark background for contrast */
  max-width: 800px;
  margin: 0 auto;
  border-collapse: collapse;
  text-align: left;
}

.users-table th,
.users-table td {
  padding: 12px;
  border-bottom: 1px solid #ddd;
}

@media (max-width: 600px) {
  .users-table th:nth-child(2),
  .users-table td:nth-child(2) {
    display: none;
  }
}

.users-table th {
  background-color: #3a3a3a;
}

.users-table td button {
  padding: 6px 12px;
  font-size: 0.9em;
}

.users-table td,
.users-table th {
  color: #f0f0f0; /* light gray text */
}

/* Error section styling */
.error-section {
  margin: 30px auto;
  max-width: 900px;
  padding: 0 15px;
}

.error-section h3 {
  color: #ff6b6b;
  text-align: center;
  margin-bottom: 15px;
  font-size: 1.2em;
}

.error-table {
  width: 100%;
  background-color: #2c2c2c;
  border-collapse: collapse;
  text-align: left;
  font-size: 0.9em;
}

.error-table th,
.error-table td {
  padding: 8px;
  border-bottom: 1px solid #ddd;
  color: #f0f0f0;
  word-wrap: break-word;
}

.error-table th {
  background-color: #3a3a3a;
  font-size: 0.85em;
}

.error-message {
  color: #ff6b6b !important;
  font-weight: bold;
  font-size: 0.8em;
  max-width: 200px;
}

.retry-btn {
  background-color: #4caf50 !important;
  color: white;
  margin-right: 5px;
  padding: 6px 10px;
  font-size: 0.8em;
  border-radius: 4px;
}

.retry-btn:hover {
  background-color: #45a049 !important;
}

.clear-btn {
  background-color: #ff6b6b !important;
  color: white;
  padding: 6px 10px;
  font-size: 0.8em;
  border-radius: 4px;
}

.clear-btn:hover {
  background-color: #ff5252 !important;
}

/* Mobile responsive design */
@media (max-width: 768px) {
  .error-section {
    margin: 20px 10px;
    padding: 0 5px;
  }
  
  .error-section h3 {
    font-size: 1.1em;
    margin-bottom: 10px;
  }

  .error-table {
    font-size: 0.8em;
  }

  .error-table th,
  .error-table td {
    padding: 6px 4px;
  }

  .error-table th {
    font-size: 0.75em;
  }

  /* Hide email column on small screens */
  .error-table th:nth-child(2),
  .error-table td:nth-child(2) {
    display: none;
  }

  .error-message {
    font-size: 0.7em;
    max-width: 150px;
  }

  .retry-btn,
  .clear-btn {
    padding: 4px 6px;
    font-size: 0.7em;
    margin: 1px;
    display: block;
    width: 100%;
    margin-bottom: 3px;
  }

  .retry-btn {
    margin-bottom: 3px;
  }
}

@media (max-width: 480px) {
  .error-section {
    margin: 15px 5px;
  }

  .error-table {
    font-size: 0.75em;
  }

  .error-table th,
  .error-table td {
    padding: 4px 2px;
  }

  /* Stack error content vertically on very small screens */
  .error-table td:nth-child(3) {
    display: block;
    padding-top: 5px;
    border-top: 1px solid #555;
  }

  .error-message {
    font-size: 0.65em;
    max-width: none;
    display: block;
    margin-bottom: 5px;
  }

  .retry-btn,
  .clear-btn {
    font-size: 0.65em;
    padding: 3px 5px;
  }
}

</style>

<script>
import DeviceRegistrationAPIService from "@/servicehandlers/DeviceRegistrationAPIService";
import AssignableDeviceList from "@/components/AssignableDeviceList.vue";
const deviceRegistrationAPIService = new DeviceRegistrationAPIService();
export default {
  components: {
    AssignableDeviceList
  },
  name: 'QrScanner',
  data() {
    return {
      result: '',
      errorMessage: '',
      showScanner: false,
      successMessage: '',
      showSuccess: false,
      unregisteredUsers: [],
      cameraError: null,
      currentIndex: 0,
      scanningUser: null,
      searchQuery: '',
      bulkMode: false,
      confirmingOverwrite: false,
      confirmationTimer: 3,
      confirmationCountdown: null,
      isSubmitting: false, // Loading state for submission
      lastScannedCode: null, // Track last scanned code to allow duplicates
      userErrors: {}, // Track errors for specific users { user_id: error_message }
      usersWithShippingErrors: [], // Users who had shipping errors but registration was created

      cameraConstraints: {
        video: {
          facingMode: { ideal: "environment" }, // Rear camera on mobile
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      },
      cameraInitialized: false
    };
  },
  computed: {
    currentUser() {
      return this.unregisteredUsers[this.currentIndex] || null;
    },
    filteredUsers() {
      const q = this.searchQuery.toLowerCase();
      return this.unregisteredUsers.filter(user =>
          user.firstName.toLowerCase().includes(q) ||
          user.lastName.toLowerCase().includes(q) ||
          user.email.toLowerCase().includes(q)
      );
    },
    allUsers() {
      // Combine current unregistered users with previously registered users for error display
      return [...this.unregisteredUsers, ...this.registeredUsersWithErrors];
    },
    registeredUsersWithErrors() {
      // Users who had shipping errors - we need to track them separately
      return this.usersWithShippingErrors || [];
    }
  },
  mounted() {
    this.getUnregisteredUsers();
    this.preloadDeviceCount();
  },
  methods: {
    startConfirmationTimer() {
      this.confirmingOverwrite = true;
      this.confirmationTimer = 3;

      this.confirmationCountdown = setInterval(() => {
        if (this.confirmationTimer > 0) {
          this.confirmationTimer -= 1;
        } else {
          clearInterval(this.confirmationCountdown);
        }
      }, 1000);
    },

    resetConfirmation() {
      this.confirmingOverwrite = false;
      this.confirmationTimer = 3;
      if (this.confirmationCountdown) clearInterval(this.confirmationCountdown);
    },

    onDecode(decodedString) {
      // Only process scans when scanner is actually visible to user
      if (!this.showScanner) {
        console.log('Ignoring scan - scanner not visible');
        return;
      }
      
      const newSerial = decodedString.replace(/^SN:/, '');
      console.log('QR Code scanned:', newSerial);
      
      // Always update the result to show what was scanned
      this.result = newSerial;
      this.lastScannedCode = newSerial;
      
      // Clear any previous error states when new code is scanned
      this.errorMessage = '';
      this.confirmingOverwrite = false;
      this.confirmationTimer = 3;
      
      if (this.confirmationCountdown) {
        clearInterval(this.confirmationCountdown);
        this.confirmationCountdown = null;
      }
    },
    resetScannerState() {
      this.errorMessage = '';
      this.confirmingOverwrite = false;
      this.confirmationTimer = 3;

      if (this.confirmationCountdown) {
        clearInterval(this.confirmationCountdown);
        this.confirmationCountdown = null;
      }
    },
    onInit(promise) {
      // Only initialize once to preserve focus
      if (this.cameraInitialized) {
        console.log('Camera already initialized, skipping reinit to preserve focus');
        return;
      }
      
      console.log('Initializing camera for the first time');
      promise
        .then(() => {
          console.log('Camera initialized and focused - will preserve this state');
          this.cameraInitialized = true;
          this.cameraError = null;
        })
        .catch(error => {
          console.error('Camera initialization failed:', error);
          this.cameraError = error;
          this.errorMessage = 'Could not access camera. Please allow camera permissions and try again.';
          this.cameraInitialized = false; // Allow retry on error
        });
    },
    async getUnregisteredUsers() {
      try {
        const response = await deviceRegistrationAPIService.getUnregisteredUsers(this.$router);
        this.unregisteredUsers = response?.users || [];
      } catch (error) {
        this.unregisteredUsers = [];
      }
    },
    openQuickScanner() {
      // Ensure scanner is hidden first
      this.showScanner = false;
      
      // Use nextTick to ensure state is clear before showing
      this.$nextTick(() => {
        this.result = '';
        this.errorMessage = '';
        this.lastScannedCode = null;
        this.bulkMode = true;
        this.scanningUser = this.currentUser;
        
        // Reset scanner's internal content cache without touching camera
        this.resetScannerContentCache();
        
        // Show scanner after state is ready
        this.showScanner = true;
      });
    },
    openScannerForUser(user) {
      // Ensure scanner is hidden first
      this.showScanner = false;
      
      if (this.confirmationCountdown) {
        clearInterval(this.confirmationCountdown);
        this.confirmationCountdown = null;
      }
      
      // Use nextTick to ensure state is clear before showing
      this.$nextTick(() => {
        this.result = '';
        this.errorMessage = '';
        this.confirmingOverwrite = false;
        this.confirmationTimer = 3;
        this.lastScannedCode = null;
        this.bulkMode = false;
        this.scanningUser = user;
        
        // Reset scanner's internal content cache without touching camera
        this.resetScannerContentCache();
        
        // Show scanner after state is ready
        this.showScanner = true;
      });
    },
    closeScanner() {
      // Don't close if we're in the middle of submitting
      if (this.isSubmitting) {
        return;
      }
      
      // Don't destroy the scanner, just hide the modal
      this.showScanner = false;
      this.result = '';
      this.scanningUser = null;
      this.errorMessage = '';
      this.confirmingOverwrite = false;
      this.confirmationTimer = 3;
      this.isSubmitting = false;

      if (this.confirmationCountdown) {
        clearInterval(this.confirmationCountdown);
        this.confirmationCountdown = null;
      }
    },
    async submitRegistration() {
      if (!this.result || !this.scanningUser || this.isSubmitting) return;

      const serial = this.result.replace(/^SN:/, '');
      this.isSubmitting = true;

      try {
        const status = await deviceRegistrationAPIService.checkDeviceStatus(serial, this.$router);

        if (status?.registered && !this.confirmingOverwrite) {
          this.errorMessage = `⚠️ Device ${serial} is already registered.`;
          this.startConfirmationTimer(); // wait 3 sec, then allow to confirm
          return;
        }

        // Proceed with registration
        const data = {
          user_id: this.scanningUser.id,
          serial_number: serial,
        };

        const response = await deviceRegistrationAPIService.postUserRegistration(data, this.$router);

        if (!response?.success) {
          const message = response?.message || 'Unknown error occurred during registration.';
          this.errorMessage = `Registration failed: ${message}`;
          return;
        }

        this.successMessage = `✅ Registered device ${serial} to ${this.scanningUser.firstName} ${this.scanningUser.lastName}`;
        this.showSuccess = true;

        setTimeout(() => {
          this.showSuccess = false;
        }, 3000);

        this.unregisteredUsers = this.unregisteredUsers.filter(u => u.id !== this.scanningUser.id);

        if (this.bulkMode) {
          // Check if there are more users to register
          const nextUser = this.unregisteredUsers[0] || null;
          
          if (nextUser) {
            // Move to next user in bulk mode
            console.log('Moving to next user in bulk mode:', nextUser.firstName, nextUser.lastName);
            this.result = '';
            this.resetScannerState(); // Clear state for next user
            this.lastScannedCode = null; // Clear to allow same code for next user
            this.scanningUser = nextUser;
            // Keep scanner open and ready for next QR code
          } else {
            // No more users - exit bulk mode and close scanner
            console.log('Bulk mode complete - no more users');
            this.bulkMode = false;
            this.showScanner = false;
          }
        } else {
          // Single user mode - reset scanner state like bulk mode, then close with delay
          console.log('Single user registration complete - resetting scanner state, closing in 2 seconds');
          this.result = '';
          this.resetScannerState(); // Same reset as bulk mode
          this.scanningUser = null; // Clear current user
          
          setTimeout(() => {
            this.closeScanner();
          }, 2000); // 2 second delay to show success message
        }

        // Reset confirmation state
        this.resetConfirmation();
      } catch (error) {
        const errorData = error?.response?.data || {};
        const message = errorData.message || 'Unexpected error during registration.';
        const errorType = errorData.error_type;
        
        console.error('Registration error:', error);
        
        // Handle shipping errors differently - add to userErrors for table display
        if (errorType === 'shipping_failed') {
          // Store error for this user to display in table
          this.$set(this.userErrors, this.scanningUser.id, `Shipping failed: ${errorData.error}`);
          
          // Add user to shipping errors list for tracking
          this.usersWithShippingErrors.push(this.scanningUser);
          
          // Remove user from unregistered list since registration was created
          this.unregisteredUsers = this.unregisteredUsers.filter(u => u.id !== this.scanningUser.id);
          
          // Show specific shipping error in scanner
          this.errorMessage = `Registration created but shipping failed: ${errorData.error}`;
          
          // Close scanner after delay to show error
          setTimeout(() => {
            this.closeScanner();
          }, 3000);
        } else {
          // General registration error - show in scanner
          this.errorMessage = `Registration failed: ${message}`;
        }
      } finally {
        this.isSubmitting = false;
      }
    },
    clearErrorAndRescan() {
      console.log('Clearing error and preparing for new scan');
      this.errorMessage = '';
      this.result = '';
      this.resetScannerState();
      // Scanner is now ready for a new QR code
    },
    preloadDeviceCount() {
      // This method exists in the original file but appears to be empty/undefined
      // Adding empty implementation to prevent the error
    },
    resetScannerContentCache() {
      // Force a fake scan event to reset the internal contentBefore cache
      // This tricks the scanner into thinking it scanned something different
      console.log('Resetting scanner content cache');
      
      // Wait for scanner to be active then simulate a reset
      this.$nextTick(() => {
        if (this.$refs.qrScanner) {
          try {
            // Trigger the scanner's onDetect with null content to reset cache
            this.$refs.qrScanner.onDetect(Promise.resolve({ content: null, location: null }));
          } catch (error) {
            console.log('Could not reset scanner cache:', error);
          }
        }
      });
    },
    getUserById(userId) {
      // Look in both unregistered users and users with shipping errors
      return this.allUsers.find(user => user.id == userId);
    },
    retryShipping(userId) {
      // This would require a new API endpoint to retry shipping for an existing registration
      console.log('Retry shipping for user:', userId);
      // For now, just remove from error list - implement retry logic later
      this.$delete(this.userErrors, userId);
      this.usersWithShippingErrors = this.usersWithShippingErrors.filter(u => u.id != userId);
    },
    clearUserError(userId) {
      // Remove user from error tracking
      this.$delete(this.userErrors, userId);
      this.usersWithShippingErrors = this.usersWithShippingErrors.filter(u => u.id != userId);
    }
  }
}
</script>